import { enqueueSnackbar } from 'notistack';
import { useEffect, useState, useCallback } from 'react';

import { useAuth } from '@/context/AuthProvider';
import { environment } from '@/environments/environment';

export interface VendorSearchRequest {
  type: 'vendor_search_request';
  search_query: string;
  address: string;
  from_call: boolean;
  call_sid?: string;
  timestamp: string;
}

export interface UseVendorSearchStreamReturn {
  lastSearchRequest: VendorSearchRequest | null;
  isConnected: boolean;
  error: Error | null;
  clearLastRequest: () => void;
}

/**
 * Hook to connect to the vendor search SSE stream
 * Receives real-time vendor search requests from Meet Terri calls
 * @param sessionToken - Optional session token for anonymous users from Meet Terri
 */
export function useVendorSearchStream(sessionToken?: string | null): UseVendorSearchStreamReturn {
  const { session } = useAuth();
  const [lastSearchRequest, setLastSearchRequest] = useState<VendorSearchRequest | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const clearLastRequest = useCallback(() => {
    setLastSearchRequest(null);
  }, []);

  useEffect(() => {
    // Connect if we have either auth token or session token
    if (!session?.access_token && !sessionToken) return;

    // Create the SSE URL with appropriate token
    let sseUrl = `${environment.api}/meet-terri/vendor-search-stream`;
    
    // Add query parameters based on available tokens
    const params = new URLSearchParams();
    if (session?.access_token) {
      params.append('token', session.access_token);
    }
    if (sessionToken) {
      params.append('session_token', sessionToken);
    }
    
    sseUrl = `${sseUrl}?${params.toString()}`;

    // If environment.api is a relative URL (starts with /), prepend the origin
    if (environment.api.startsWith('/')) {
      sseUrl = `${window.location.origin}${environment.api}/meet-terri/vendor-search-stream?${params.toString()}`;
    }

    const eventSource = new EventSource(sseUrl, {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      setIsConnected(true);
      // Connected to vendor search stream
    };

    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data) as VendorSearchRequest;
        
        if (data.type === 'vendor_search_request') {
          // Received vendor search request
          setLastSearchRequest(data);
          
          // Show notification that a search request was received
          enqueueSnackbar('Meet Terri detected a vendor search request!', {
            variant: 'info',
            autoHideDuration: 5000,
          });
        }
      } catch (err) {
        // Error parsing vendor search event
      }
    };

    eventSource.onerror = e => {
      const err = new Error('Vendor search stream connection error');
      setError(err);
      setIsConnected(false);
      // Vendor search stream error
      eventSource.close();
    };

    return () => {
      eventSource.close();
      setIsConnected(false);
    };
  }, [session?.access_token, sessionToken]);

  return { 
    lastSearchRequest, 
    isConnected, 
    error, 
    clearLastRequest 
  };
}